import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  ShoppingCart, LayoutDashboard, Users, FileText, Receipt, CalendarDays,
  Package, BarChart3, Settings, LogOut, Menu, X, ClipboardList,
  RotateCcw, Gift, Banknote, Truck, Store, Image, Building2, Tags, DollarSign, BookOpen, Archive, Scale, HardHat, Contact, PenLine,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useRole } from '../../context/RoleContext'
import { THOMSONS_LOGO, POS_BACKGROUND } from '../../lib/assets'
import type { UserRole } from '../../types'

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  roles: UserRole[]
}

interface NavSection {
  heading: string
  items: NavItem[]
}

const ALL: UserRole[] = ['cashier', 'manager', 'admin']
const MGR: UserRole[] = ['manager', 'admin']
const ADM: UserRole[] = ['admin']

// Merged POS + IMS information architecture (see PORT_SPEC.md).
const NAV: NavSection[] = [
  {
    heading: 'Point of Sale',
    items: [
      { to: '/sales', label: 'Sales Register', icon: <ShoppingCart size={18} />, roles: ALL },
      { to: '/transactions', label: 'Transactions', icon: <Receipt size={18} />, roles: ALL },
      { to: '/refunds', label: 'Refunds / Void', icon: <RotateCcw size={18} />, roles: MGR },
      { to: '/gift-cards', label: 'Gift Cards', icon: <Gift size={18} />, roles: ALL },
      { to: '/payouts', label: 'Payouts', icon: <Banknote size={18} />, roles: MGR },
    ],
  },
  {
    heading: 'Customers & Jobs',
    items: [
      { to: '/customers', label: 'Customers', icon: <Users size={18} />, roles: ALL },
      { to: '/appointments', label: 'Appointments', icon: <CalendarDays size={18} />, roles: MGR },
      { to: '/estimates', label: 'Estimates', icon: <FileText size={18} />, roles: MGR },
      { to: '/bills', label: 'Job Invoices', icon: <ClipboardList size={18} />, roles: MGR },
      { to: '/workers', label: 'Workers / Crew', icon: <HardHat size={18} />, roles: MGR },
      { to: '/rolodex', label: 'Rolodex', icon: <Contact size={18} />, roles: MGR },
      { to: '/site-plans', label: 'Site Plans', icon: <PenLine size={18} />, roles: MGR },
    ],
  },
  {
    heading: 'Inventory',
    items: [
      { to: '/products', label: 'Products', icon: <Package size={18} />, roles: MGR },
      { to: '/price-manager', label: 'Price Manager', icon: <DollarSign size={18} />, roles: MGR },
      { to: '/price-book', label: 'Price Book', icon: <BookOpen size={18} />, roles: MGR },
      { to: '/inventory-lifecycle', label: 'Lifecycle / EOY', icon: <Archive size={18} />, roles: MGR },
      { to: '/inventory-pictures', label: 'Inventory Pictures', icon: <Image size={18} />, roles: MGR },
      { to: '/vendors', label: 'Vendors', icon: <Truck size={18} />, roles: MGR },
      { to: '/receiving', label: 'Receiving', icon: <Store size={18} />, roles: MGR },
    ],
  },
  {
    heading: 'Insights',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, roles: MGR },
      { to: '/reports', label: 'Reports', icon: <BarChart3 size={18} />, roles: MGR },
      { to: '/reconciliation', label: 'Reconciliation', icon: <Scale size={18} />, roles: MGR },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/departments', label: 'Departments', icon: <Building2 size={18} />, roles: ADM },
      { to: '/categories', label: 'Categories', icon: <Tags size={18} />, roles: ADM },
      { to: '/admin', label: 'Users & Logs', icon: <Users size={18} />, roles: ADM },
      { to: '/settings', label: 'Settings', icon: <Settings size={18} />, roles: ADM },
    ],
  },
]

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth()
  const { role } = useRole()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  // Build visible sections (filter items by role, drop empty sections).
  const sections = role
    ? NAV.map(s => ({ heading: s.heading, items: s.items.filter(i => i.roles.includes(role)) }))
        .filter(s => s.items.length > 0)
    : []

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-green-900 text-green-50 flex flex-col transform transition-transform duration-200 ${
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex items-center gap-2 px-5 h-16 border-b border-green-800">
          <span className="flex items-center justify-center h-9 w-9 rounded-full bg-white">
            <img src={THOMSONS_LOGO} alt="Thomson's" className="h-7 w-7 object-contain" />
          </span>
          <span className="font-bold text-lg tracking-tight">Thomson's</span>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {sections.length > 0 ? (
            sections.map(section => (
              <div key={section.heading}>
                <p className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-green-400">
                  {section.heading}
                </p>
                <div className="space-y-0.5">
                  {section.items.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive ? 'bg-green-700 text-white' : 'text-green-100 hover:bg-green-800'
                        }`
                      }
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-sm text-green-200">
              No role assigned to this account. Ask an administrator to set your role, then sign out and back in.
            </div>
          )}
        </nav>
        <div className="border-t border-green-800 p-3">
          <div className="px-2 py-2 text-xs text-green-300 truncate">{user?.email}</div>
          <div className="px-2 pb-2 text-xs">
            <span className="inline-block px-2 py-0.5 rounded-full bg-green-700 text-green-50 capitalize">{role ?? 'no role'}</span>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-green-100 hover:bg-green-800 transition-colors"
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-black/40 z-20 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-6 gap-3 sticky top-0 z-10">
          <button className="lg:hidden text-gray-600" onClick={() => setOpen(o => !o)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <img src={THOMSONS_LOGO} alt="" className="h-9 w-9 object-contain" />
          <h1 className="text-lg font-semibold text-gray-800">Thomson's Landscaping & Garden Center</h1>
        </header>
        <main
          className="flex-1 overflow-y-auto p-4 lg:p-6 bg-no-repeat bg-cover bg-center bg-fixed"
          style={{ backgroundImage: `linear-gradient(rgba(247,248,247,0.82), rgba(247,248,247,0.92)), url(${POS_BACKGROUND})` }}
        >
          {children}
        </main>
      </div>
    </div>
  )
}
